import { Component, EventEmitter, Input, OnInit, Output, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { WarehouseService } from '../../../_services/warehouse.service';
import { TicketArticleHandlingService } from '../../../_services/ticket-article-handling.service';
import { ArticleSummary, Warehouse, StorageBin } from '../../../_models';

/**
 * Modal-Dialog zum Stornieren einer einzelnen installierten Material-Buchung.
 *
 * Office-User waehlt Ziel-Lager + Lagerplatz, optional "Bad Part"-Markierung.
 * Backend bucht Article zurueck, loescht ArticleHandling und schreibt eine
 * Ticket-Note. Bei Erfolg emittet die Komponente die aktualisierte
 * article_summary an den Parent.
 */
@Component({
  standalone: true,
  imports: [CommonModule, FormsModule],
  selector: 'hawk-cancel-installation-dialog',
  templateUrl: './cancel-installation-dialog.component.html',
})
export class CancelInstallationDialogComponent implements OnInit {
  @Input({ required: true }) ticketId!: number;
  @Input({ required: true }) articleHandlingId!: number;
  @Input() articleDescription = '';
  @Input() articleSn = '';

  @Output() cancelled = new EventEmitter<ArticleSummary>();
  @Output() closed = new EventEmitter<void>();

  protected warehouses = signal<Warehouse[]>([]);
  protected storageBins = signal<StorageBin[]>([]);
  protected selectedWarehouseId = signal<number | null>(null);
  protected selectedStorageBinId = signal<number | null>(null);
  protected badPart = signal<boolean>(false);
  protected badPartDescription = signal<string>('');

  protected loadingWarehouses = signal<boolean>(false);
  protected loadingBins = signal<boolean>(false);
  protected submitting = signal<boolean>(false);
  protected errorMessage = signal<string | null>(null);

  protected canSubmit = computed(() =>
    this.selectedWarehouseId() != null &&
    this.selectedStorageBinId() != null &&
    !this.submitting(),
  );

  constructor(
    private warehouseService: WarehouseService,
    private handlingService: TicketArticleHandlingService,
  ) {}

  ngOnInit(): void {
    this.loadingWarehouses.set(true);
    this.warehouseService.loadWarehouses().subscribe({
      next: (data) => {
        this.warehouses.set([...data].sort((a, b) => (a.label || a.name || '').localeCompare(b.label || b.name || '')));
        this.loadingWarehouses.set(false);
      },
      error: (err) => {
        console.error('Lager konnten nicht geladen werden', err);
        this.errorMessage.set('Lager konnten nicht geladen werden.');
        this.loadingWarehouses.set(false);
      },
    });
  }

  onWarehouseChange(warehouseId: number | null): void {
    this.selectedWarehouseId.set(warehouseId);
    this.selectedStorageBinId.set(null);
    this.storageBins.set([]);
    if (warehouseId == null) return;

    this.loadingBins.set(true);
    this.warehouseService.loadStorageBins(warehouseId).subscribe({
      next: (data) => {
        this.storageBins.set([...data].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        this.loadingBins.set(false);
      },
      error: (err) => {
        console.error('Lagerplaetze konnten nicht geladen werden', err);
        this.errorMessage.set('Lagerplaetze konnten nicht geladen werden.');
        this.loadingBins.set(false);
      },
    });
  }

  submit(): void {
    if (!this.canSubmit()) return;
    this.errorMessage.set(null);
    this.submitting.set(true);

    this.handlingService.cancelInstallation(this.ticketId, this.articleHandlingId, {
      target_warehouse_id: this.selectedWarehouseId()!,
      target_storage_bin_id: this.selectedStorageBinId()!,
      bad_part: this.badPart(),
      bad_part_description: this.badPart() ? this.badPartDescription().trim() : undefined,
    }).subscribe({
      next: (summary) => {
        this.submitting.set(false);
        this.cancelled.emit(summary);
      },
      error: (err) => {
        console.error('Storno fehlgeschlagen', err);
        this.errorMessage.set(err?.error?.error || 'Storno fehlgeschlagen.');
        this.submitting.set(false);
      },
    });
  }

  close(): void {
    if (this.submitting()) return;
    this.closed.emit();
  }
}
